<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ToastProps } from '../../models/feedback/Toast.model'
import { defaultToastProps } from '../../models/feedback/Toast.model'
import AppIcon from '../elements/AppIcon.vue'

const props = withDefaults(defineProps<ToastProps>(), defaultToastProps)
const { t } = useI18n()
const emit = defineEmits(['close'])

const variants = computed(() => [
  'w-full shadow-md rounded-lg sm:w-[400px]',
  'grid grid-cols-[auto_1fr_auto] gap-2 py-3 px-4',
  {
    'bg-signal-error-50': props.variant === 'error',
    'bg-signal-success-50': props.variant === 'success',
    'bg-signal-warning-50': props.variant === 'warning',
    'bg-signal-info-50': props.variant === 'info',
  },
])

// TODO #PORTAL-475: Replace icons
// Equivalent Font Awesome icons are only included with Pro license.
// Replace heroicons with Design Library icons asap.
const iconName = computed(() => {
  switch (props.variant) {
    case 'success':
      return 'heroicons:check-circle-solid'
    case 'error':
    case 'warning':
      return 'heroicons:exclamation-triangle-solid'
    default:
      return 'heroicons:information-circle-solid'
  }
})

const role = computed(() => {
  if (props.ariaLive === 'assertive') {
    return 'alert'
  }
  switch (props.variant) {
    case 'error':
    case 'warning':
      return 'alert'
    default:
      return 'status'
  }
})
</script>

<template>
  <div :class="variants" :role="role" :aria-live="props.ariaLive">
    <div>
      <AppIcon :name="iconName" color="block accent-01" size="md" />
    </div>
    <div>
      <template v-if="headline || description">
        <h3 v-if="headline" class="text-base font-semibold">
          {{ headline }}
        </h3>
        <p v-if="description">
          {{ description }}
        </p>
      </template>
      <slot v-else />
    </div>
    <div>
      <button
        :class="[
          'flex size-6 items-center justify-center rounded-lg',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black focus-visible:ring-offset-0',
        ]"
        :aria-label="t('toast.close')"
        @click="emit('close')"
      >
        <AppIcon name="heroicons:x-mark" color="accent-01" size="sm" />
      </button>
    </div>
  </div>
</template>
